import {
  Component, OnDestroy, OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  forwardRef,
  Input,
  signal
} from '@angular/core';
import { Subscription, BehaviorSubject } from 'rxjs';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { all } from '@awesome.me/kit-787edf9aa0/icons';
import { AnimationProp, FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { captchaLength } from '../../common/constants.lib';
import { ToUppperCaseDirective } from "../../directives/to-uppper-case.directive";
import { CaptchaServiceService } from '../../shared/captcha-service.service';
import { captchaWaitInterval } from '../../setup/config';
import { LoggerService } from '../../shared/logger/logger.service';

@Component({
  selector: 'captcha-required',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CaptchaRequiredComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => CaptchaRequiredComponent)
    }
  ],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonsModule,
    FontAwesomeModule,
    ToUppperCaseDirective
  ],
  templateUrl: './captcha-required.component.html',
  styleUrl: './captcha-required.component.scss'
})
export class CaptchaRequiredComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor, Validator {

  //#region Properties

  disableButton: boolean = false;
  spinOptions: AnimationProp = 'spin';
  captchaText: string = '';
  disabled!: boolean;
  touched = false;

  private _setFocus: number = 0;
  @Input() set setFocus(val: number) {
    // this.logger.debug(`set setFocus: `, val);
    this._setFocus = val;
    this.setTextFocus();
  }
  get setFocus(): number | undefined {
    return this._setFocus;
  };

  private _subs: Subscription[] = [];

  private _captchaImage: BehaviorSubject<string> = new BehaviorSubject<string>('');
  get captchaImage() {
    return this._captchaImage.getValue();
  }
  set captchaImage(val: string) {
    this.logger.debug(`setter: captchaimage: val: `, val);
    this._captchaImage.next(val);
  }

  @ViewChild('captchainputfocus') captchainputfocus!: ElementRef;

  //#endregion

  constructor(
    private logger: LoggerService,
    private captchaService: CaptchaServiceService,
    public faIcons: FaIconLibrary,

  ) {
    faIcons.addIcons(...all);
  }

  //#region Ng Events

  ngOnInit(): void {
    this._subs.push(this.captchaService.captchaImage$.subscribe(image => {
      this.logger.debug(`ngOnInit:subscription:image: `, image);
      this.captchaImage = image;
    }));
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges): void { }

  //#endregion

  //#region Event Handlers

  onTouched: any = () => { };

  onChange = (val: string | undefined) => { };

  writeValue(value: string): void {
    this.logger.debug(`writeValue: `, value, this.touched);
    if (!value) {
      this.fetchCaptchaImage();
      this.setTextFocus();
    }
    this.captchaText = value;
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(event: Event) {
    const val = (event.target as HTMLInputElement).value;
    this.logger.debug('onInput: ', val);
    this.captchaText = val;
    this.onChange(val); // Propagate change to parent form
    this.markAsTouched();
  }

  onBlur() {
    this.onTouched();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const val = control.value;
    // Check for "required" logic internally
    if (!val || (typeof val === 'string' && val.trim() === '')) {
      return { 'required': true };
    }
    return null; // Valid
  }

  //#endregion

  //#region Exposed methods

  refreshCaptcha() {
    this.logger.debug(`refreshCaptcha: `);
    this.resetFlags();
    this.fetchCaptchaImage();
    this.markAsTouched();
    this.setTextFocus();
  };

  //#endregion

  //#region Private methods

  private fetchCaptchaImage() {
    this.logger.debug(`fetchCaptchaImage...`);
    this.disableButton = true;
    setTimeout(() => {
      this.captchaService.fetchCaptcha()
        .then(image => {
          this.logger.debug(`fetchCaptchaImage:fetched: `, image);
          setTimeout(() => {
            this.captchaImage = image;
            this.disableButton = false;
          })
        })
        .catch(err => {
          this.logger.error(`fetchCaptchaImage:error: `, err.message);
          this.captchaImage = '';
          this.disableButton = false;
        })
        .finally(() => {
          // this.captchainputfocus.nativeElement.focus();
        });
    }, captchaWaitInterval)
  }

  private resetFlags() {
    this.captchaText = '';
    this.onChange('');
  }

  private markAsTouched(): void {
    if (!this.touched) {
      this.touched = true;
      this.onTouched();
    }
  }

  private setTextFocus() {
    setTimeout(() => {
      // if (this.touched) {
      //   this.captchainputfocus.nativeElement.focus();
      // }
      this.captchainputfocus.nativeElement.focus();
    });
  }

  //#endregion


}
